import * as types from '../actions/action-types.js';
import isElectron from '../utils/is-electron.js';

import localforage from 'localforage';

/**
 * Reduces session-related events (requesting a new user login,
 * receiving login data – either successfully or unsucessfully),
 * and reducing it into a session object.
 *
 * @see http://redux.js.org/docs/advanced/AsyncActions.html
 * @see https://github.com/acdlite/flux-standard-action
 */

export default (state = {
  room: '',
  token: null,
  user: {},
  authenticating: false,
  authenticated: false,
  error: null,

  resetPassword: false,
  resettingPassword: false,
  resetPasswordError: null,

  updatingUser: false,
  updatedUser: false,
  updatingUserError: null
}, action) => {
  switch(action.type) {
    // Handle dispatching a network request with the given credentials to authenticate the
    // user, and properly dispatch a success or error action on response from the service.
    case types.REQUEST_USER_LOGIN_PENDING:
      return {
        ...state,
        authenticating: true,
        authenticated: false,
        error: null
      };

    case types.REQUEST_USER_LOGIN_REJECTED:
      return {
        ...state,
        authenticating: false,
        authenticated: false,
        error: action.payload
      };

    case types.REQUEST_USER_LOGIN_FULFILLED:
      localforage.setItem('session', {
        token: action.payload.access_token,
        user: action.payload.user
      });

      if (isElectron()) {
        const { ipcRenderer } = window.require('electron');
        let payload = {
          token: action.payload.access_token,
          user: action.payload.user
        };

        ipcRenderer.send('auth', payload);
      }

      return {
        ...state,
        authenticating: false,
        authenticated: true,
        token: action.payload.access_token,
        user: action.payload.user
      };

    // Log out the user - simply overwrites the session data and
    // removes any localstorage-based session data.
    case types.REQUEST_USER_LOGOUT:
      localforage.removeItem('session');
      return {
        ...state,
        user: {},
        token: null,
        errorAuthenticating: null
      };

    case types.REVIVE_SESSION:
      return {
        ...state,
        authenticating: false,
        authenticated: true,
        error: null,
        user: action.user,
        token: action.token
      };

    case types.REQUEST_VALIDATE_SESSION:
      return {
        ...state,
        authenticating: false,
        authenticated: true,
        error: null,
        user: action.user,
        token: action.token
      };

    case types.REQUEST_PASSWORD_RESET_PENDING:
      return {
        ...state,
        resetPassword: false,
        resettingPassword: true,
        resetPasswordError: null
      };

    case types.REQUEST_PASSWORD_RESET_FULFILLED:
      return {
        ...state,
        resetPassword: true,
        resettingPassword: false,
        resetPasswordError: null
      };

    case types.REQUEST_PASSWORD_RESET_REJECTED:
      return {
        ...state,
        resetPassword: false,
        resettingPassword: false,
        resetPasswordError: action.payload
      };

    case types.REQUEST_ROOM_CHANGE:
      // If we're not opening the Lobby (we don't save that room, as it's the
      // default of Normal Chat), update the locally saved `data.recentRooms`
      // array in localforage.
      if (action.room.toLowerCase() !== 'lobby') {
        localforage.getItem('data')
          .then((data = {}) => {
            let recentRooms = data.recentRooms || [];
            let otherRooms = recentRooms.filter((room) => room.name.toLowerCase() !== action.room.toLowerCase());

            // Assemble a new `recentRooms` array with the latest joined
            // room at the top of the list.
            recentRooms = [{
              name: action.room,
              lastJoined: new Date()
            }].concat(otherRooms);

            // Persist it to local storage.
            localforage.setItem('data', {
              ...data,
              recentRooms
            });
          });
      }

      return {
        ...state,
        room: action.room
      };

    case types.REQUEST_UPDATE_USER_PENDING:
      return {
        ...state,
        updatingUser: true,
        updatingUserError: null
      };

    case types.REQUEST_UPDATE_USER_REJECTED:
      return {
        ...state,
        updatingUser: false,
        updatedUser: false,
        updatingUserError: action.payload
      };

    case types.REQUEST_UPDATE_USER_FULFILLED:
      localforage.getItem('session')
        .then((session) => {
          session.user = action.payload;
          localforage.setItem('session', session);
        });

      return {
        ...state,
        user: action.payload,
        updatingUser: false,
        updatedUser: true,
        updatingUserError: null
      };

    case types.UPDATE_CACHED_USER:
      let newUser = action.meta.user || {};
      let oldUser = state.user || {};

      return {
        ...state,
        user: {
          ...oldUser,
          ...newUser
        }
      };

    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/reducers/session-reducer.js